import { useCallback, useEffect, useState } from 'react';
import { Box, Text, Alert, AlertIcon, AlertTitle } from '@chakra-ui/react';

import { ImmigrationStatusShareCodeDoc } from 'types';
import { getDateFromString, getStringFromDate } from '../../../../utilities/helpers';
import { IDate, IQuestion } from '../../../../types';
import { Modal } from '../../../atoms/modal';
import { DateInputWithConfirm } from '../../../formInputs/DateInput';
import { InputWithConfirm } from '../../../formInputs/Input';
import { RadioOptionsWithConfirm } from '../../../formInputs/RadioOptions';
import { TextareaWithConfirm } from '../../../formInputs/Textarea';
import UploadActions from '../../../atoms/upload-actions';
import { validateDate } from '../../../../utilities/validateDate';
import { Link } from '../../../atoms/link';

enum SelectionOptions {
  YES = 'Yes',
  NOT_YET = 'Not yet'
}

enum GenerateSharCodeTodayOptions {
  YES = 'Yes',
  NO = 'No'
}

enum ImmigrationStatus {
  STUDENT = 'Student',
  SETTLED = 'Settled',
  PRE_SETTLED = 'Pre-settled',
  OTHER = 'Other'
}

export interface IProps {
  title: string;
  closeModal: () => void;
  docKey: string;
  shareCode: ImmigrationStatusShareCodeDoc;
  onSave: (docKey: string, data: any) => void;
}

export const ImmigrationStatusShareCodeModal = (props: IProps) => {
  const { title, closeModal, onSave, docKey, shareCode: shareCodeDoc } = props;

  const isComplete = !!shareCodeDoc?.shareCode?.length && !!shareCodeDoc?.shareCodeGenerateDate?.length;

  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [isDecisionReceived, setIsDecisionReceived] = useState<SelectionOptions | undefined>(
    isComplete ? SelectionOptions.YES : undefined
  );
  const [immigrationStatus, setImmigrationStatus] = useState<ImmigrationStatus>(
    (shareCodeDoc?.immigrationStatus ?? '') as ImmigrationStatus
  );
  const [shareCode, setShareCode] = useState(shareCodeDoc?.shareCode ?? '');
  const [generateSharCodeToday, setGenerateSharCodeToday] = useState<undefined | boolean>(undefined);
  const [shareCodeGenerateDate, setShareCodeGenerateDate] = useState(
    getDateFromString(shareCodeDoc.shareCodeGenerateDate)
  );
  const [shareCodeGenerateDateError, setShareCodeGenerateDateError] = useState('');

  const [startDate, setStartDate] = useState(getDateFromString(shareCodeDoc.startDate));
  const [startDateError, setStartDateError] = useState('');

  const [untilDate, setUntilDate] = useState(getDateFromString(shareCodeDoc.validUntil));
  const [untilDateError, setUntilDateError] = useState('');

  const [detailsOfStatusGranted, setDetailsOfStatusGranted] = useState(shareCodeDoc?.detailsOfStatusGranted ?? '');

  useEffect(() => {
    if (isChanged) {
      setStartDate({ DD: '', MM: '', YYYY: '' });
      setStartDateError('');

      setUntilDate({ DD: '', MM: '', YYYY: '' });
      setUntilDateError('');

      setDetailsOfStatusGranted('');
    }
  }, [immigrationStatus]);

  const handleQuestionsClick = (question: IQuestion) => {
    if (question.label !== isDecisionReceived) {
      setIsDecisionReceived(question.label as any);
      setIsChanged(true);
    }
  };

  const handleImmigrationsStatusClick = (question: IQuestion) => {
    if (question.label !== immigrationStatus) {
      setImmigrationStatus(question.label as any);
      setIsChanged(true);
    }
  };

  const handleGenerateSharCodeDateClick = (question: IQuestion) => {
    if ((question.label === GenerateSharCodeTodayOptions.YES) !== generateSharCodeToday) {
      setGenerateSharCodeToday(question.label === GenerateSharCodeTodayOptions.YES);
      setIsChanged(true);
    }
  };
  const returnToSection = () => {
    closeModal();
  };

  const checkDate = useCallback((date: IDate) => {
    return date.DD?.length && date.MM?.length && date.YYYY?.length === 4;
  }, []);

  const calculateIsComplete = () => {
    let status = !!shareCode.length && !!immigrationStatus.length;

    if (!generateSharCodeToday) {
      status = status && Boolean(checkDate(shareCodeGenerateDate)) && !shareCodeGenerateDateError;
    }

    if (immigrationStatus === ImmigrationStatus.STUDENT) {
      status = status && Boolean(checkDate(startDate)) && !startDateError;
    }
    if ([ImmigrationStatus.STUDENT, ImmigrationStatus.PRE_SETTLED].includes(immigrationStatus)) {
      status = status && Boolean(checkDate(untilDate)) && !untilDateError;
    }
    if (immigrationStatus === ImmigrationStatus.OTHER) {
      status = status && !!detailsOfStatusGranted.length;
    }

    return status;
  };

  const save = () => {
    let dateToSave: IDate = shareCodeGenerateDate;
    if (generateSharCodeToday) {
      const today = new Date();
      dateToSave = {
        DD: today.getDate().toString(),
        MM: (today.getMonth() + 1).toString(),
        YYYY: today.getFullYear().toString()
      };
    }
    const data: any = {
      shareCode,
      shareCodeGenerateDate: getStringFromDate(dateToSave),
      immigrationStatus: immigrationStatus,
      startDate: getStringFromDate(startDate),
      validUntil: getStringFromDate(untilDate),
      detailsOfStatusGranted
    };

    onSave(docKey, data);
  };

  return (
    <Modal id={'immigrationStatusShareCode'} show={true} title={title} onClose={closeModal} hideFooter>
      <Text data-selector={'intro-text'}>
        If you used the UK Immigration: ID Check app and are successful in your application, you will receive written
        notification by email of that decision. This will include details of how to sign in to the View and Prove
        service on gov.uk website, where you can check your immigration status.
      </Text>

      <div data-selector="isDecisionReceived-label">
        <RadioOptionsWithConfirm
          key={'isDecisionReceived'}
          value={isDecisionReceived}
          label={
            'Have you already received a decision on your application and the details for the View and Prove Service?'
          }
          onClick={handleQuestionsClick}
          isInline={true}
          maxQuestions={2}
          questions={[
            {
              char: 'Y',
              label: SelectionOptions.YES,
              selected: isDecisionReceived === SelectionOptions.YES
            },
            {
              char: 'N',
              label: SelectionOptions.NOT_YET,
              selected: isDecisionReceived === SelectionOptions.NOT_YET
            }
          ]}
        />
      </div>

      {isDecisionReceived === SelectionOptions.YES ? (
        <>
          <Alert status="info" data-selector="create-share-code-link-wrapper" mb={6}>
            <AlertIcon />

            <Box>
              <AlertTitle>Create your share code</AlertTitle>

              <Text>First, please create a new share code using the following link:</Text>

              <Text>
                <Link href={'https://www.gov.uk/view-prove-immigration-status'} isExternal>
                  View and Prove your Immigration Status
                </Link>
              </Text>

              <Text mb={0}>You will need to return to this page to enter the share code below.</Text>
            </Box>
          </Alert>

          <InputWithConfirm
            label="Share code"
            subText="Please enter the share code generated on gov.uk website"
            value={shareCode}
            onChange={(evt: React.FormEvent<HTMLInputElement>) => {
              setShareCode(evt.currentTarget.value);
              setIsChanged(true);
            }}
          />

          {!isComplete && (
            <div data-selector="generateSharCodeToday-questions">
              <RadioOptionsWithConfirm
                key={'generateSharCodeToday'}
                value={generateSharCodeToday}
                label={'Did you generate this share code today? '}
                onClick={handleGenerateSharCodeDateClick}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: GenerateSharCodeTodayOptions.YES,
                    selected: generateSharCodeToday
                  },
                  {
                    char: 'N',
                    label: GenerateSharCodeTodayOptions.NO,
                    selected: generateSharCodeToday === false
                  }
                ]}
              />
            </div>
          )}

          {(generateSharCodeToday === false || isComplete) && (
            <DateInputWithConfirm
              id={`shareCode-generate-date`}
              label={`When did you generate this share code?`}
              value={shareCodeGenerateDate}
              errorText={shareCodeGenerateDateError}
              onChangeDD={(evt: any) => {
                setShareCodeGenerateDate({
                  ...shareCodeGenerateDate,
                  DD: evt.target.value
                });
                setIsChanged(true);
              }}
              onChangeMM={(evt: any) => {
                setShareCodeGenerateDate({
                  ...shareCodeGenerateDate,
                  MM: evt.target.value
                });
                setIsChanged(true);
              }}
              onChangeYY={(evt: any) => {
                setShareCodeGenerateDate({
                  ...shareCodeGenerateDate,
                  YYYY: evt.target.value
                });
                setIsChanged(true);
              }}
              onBlur={() => setShareCodeGenerateDateError(validateDate(shareCodeGenerateDate))}
            />
          )}

          <RadioOptionsWithConfirm
            key={'immigrationStatus'}
            value={immigrationStatus}
            label={'Which Immigration status have you been granted?'}
            onClick={handleImmigrationsStatusClick}
            isInline={false}
            questions={[
              {
                char: '1',
                label: ImmigrationStatus.STUDENT,
                selected: immigrationStatus === ImmigrationStatus.STUDENT
              },
              {
                char: '2',
                label: ImmigrationStatus.SETTLED,
                selected: immigrationStatus === ImmigrationStatus.SETTLED
              },
              {
                char: '3',
                label: ImmigrationStatus.PRE_SETTLED,
                selected: immigrationStatus === ImmigrationStatus.PRE_SETTLED
              },
              {
                char: '4',
                label: ImmigrationStatus.OTHER,
                selected: immigrationStatus === ImmigrationStatus.OTHER
              }
            ]}
          />

          {immigrationStatus === ImmigrationStatus.STUDENT && (
            <DateInputWithConfirm
              id={`immigrationStatus-start-date`}
              label={`What is the valid from date for this immigration status?`}
              value={startDate}
              errorText={startDateError}
              onChangeDD={(evt: any) => {
                setStartDate({
                  ...startDate,
                  DD: evt.target.value
                });
                setIsChanged(true);
              }}
              onChangeMM={(evt: any) => {
                setStartDate({
                  ...startDate,
                  MM: evt.target.value
                });
                setIsChanged(true);
              }}
              onChangeYY={(evt: any) => {
                setStartDate({
                  ...startDate,
                  YYYY: evt.target.value
                });
                setIsChanged(true);
              }}
              onBlur={() => setStartDateError(validateDate(startDate))}
            />
          )}

          {[ImmigrationStatus.STUDENT, ImmigrationStatus.PRE_SETTLED].includes(immigrationStatus) && (
            <DateInputWithConfirm
              id={`immigrationStatus-valid-until`}
              label={`What is the valid until date for this immigration status?`}
              value={untilDate}
              errorText={untilDateError}
              onChangeDD={(evt: any) => {
                setUntilDate({
                  ...untilDate,
                  DD: evt.target.value
                });
                setIsChanged(true);
              }}
              onChangeMM={(evt: any) => {
                setUntilDate({
                  ...untilDate,
                  MM: evt.target.value
                });
                setIsChanged(true);
              }}
              onChangeYY={(evt: any) => {
                setUntilDate({
                  ...untilDate,
                  YYYY: evt.target.value
                });
                setIsChanged(true);
              }}
              onBlur={() => setUntilDateError(validateDate(untilDate))}
            />
          )}

          {immigrationStatus === ImmigrationStatus.OTHER && (
            <TextareaWithConfirm
              id={'immigration-status-details'}
              label={
                'Please enter the status granted as well as any start or valid until dates for this status (if available)'
              }
              onChange={(evt: any) => {
                setDetailsOfStatusGranted(evt.currentTarget.value);
                setIsChanged(true);
              }}
              value={detailsOfStatusGranted}
            />
          )}

          {isChanged ? (
            <div data-selector="yes-button">
              <UploadActions complete={true} label={undefined} disabled={!calculateIsComplete()} onContinue={save} />
            </div>
          ) : (
            <div>
              <UploadActions complete={false} label={undefined} disabled={false} onContinue={returnToSection} />
            </div>
          )}
        </>
      ) : (
        <>
          <Alert status="info" data-selector="not-yet-wrapper" mb={6}>
            If you have not received the decision on your application yet, please come back to this page once you have
            received it.
          </Alert>

          <div data-selector="not-yet-button">
            <UploadActions complete={false} label={undefined} disabled={false} onContinue={returnToSection} />
          </div>
        </>
      )}
    </Modal>
  );
};
